import { MouseEvent, useCallback } from 'react';
import './Button.scss';
import SvgIcon from '../SvgIcon/SvgIcon';
import useDropdown from '../../hooks/useDropdown/useDropdown';
import { svgReferences } from '../../utils/svg/svg';
import { getIconColour } from '../../utils/icons/icons';
import OptionsList from '../Dropdown/DropdownComponents/OptionsList/OptionsList';
import Text from '../Text/Text';

const Button = ({
  buttonStyleType,
  label,
  iconSize = 12,
  leftIconName,
  rightIconName,
  buttonSmall = false,
  disabled = false,
  handleClick,
  accessibilityLabel = '',
  colour,
  type = 'button',
  dataTestId = '',
  tabIndex = 0,
  withDropdown = false,
  rightAligned = true,
  dropdownOptions = [],
  dropdownSave = false,
  handleOptionClick,
  dropdownOpenIconName,
  customStyling,
  customClassName,
}: {
  buttonStyleType: 'primary' | 'secondary' | 'text';
  label: string;
  handleClick?: (v: MouseEvent<HTMLButtonElement>) => void;
  leftIconName?: keyof typeof svgReferences;
  rightIconName?: keyof typeof svgReferences;
  buttonSmall?: boolean;
  disabled?: boolean;
  accessibilityLabel?: string;
  colour?: string;
  type?: 'button' | 'submit' | 'reset';
  dataTestId?: string;
  iconSize?: number;
  tabIndex?: number;
  withDropdown?: boolean;
  rightAligned?: boolean;
  dropdownOptions?: {
    value: string;
    label: string;
    disabled: boolean;
  }[];
  dropdownSave?: boolean;
  handleOptionClick?: (v: unknown) => void;
  dropdownOpenIconName?: keyof typeof svgReferences;
  customStyling?: object;
  customClassName?: string;
}) => {
  const getButtonClassList = () => {
    const classList = ['button callout'];
    classList.push(`button-${buttonStyleType}`);
    if (leftIconName) classList.push('button-left-icon');
    if (rightIconName) classList.push('button-right-icon');
    if (buttonSmall) classList.push('button-small');
    if (disabled) classList.push('disabled');
    if (colour === 'var(--success)') classList.push('success');
    if (colour === 'var(--error)') classList.push('error');
    if (customClassName) classList.push(customClassName);
    return classList.join(' ');
  };

  const { dropdownOptionsListRef, isDropdownOpen, toggleDropdown } = useDropdown();

  const renderSvgIcon = useCallback(
    (iconName: keyof typeof svgReferences) => {
      return (
        <SvgIcon
          iconName={isDropdownOpen && dropdownOpenIconName ? dropdownOpenIconName : iconName}
          fill={getIconColour(buttonStyleType, disabled)}
          width={iconSize}
          height={iconSize}
        />
      );
    },
    [buttonStyleType, disabled, dropdownOpenIconName, iconSize, isDropdownOpen],
  );

  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (withDropdown) toggleDropdown();
    handleClick?.(e);
  };

  return (
    <>
      <button
        style={customStyling}
        disabled={disabled}
        className={getButtonClassList()}
        onClick={onClick}
        aria-label={accessibilityLabel}
        type={type}
        data-testid={dataTestId}
        tabIndex={tabIndex}
      >
        {leftIconName && renderSvgIcon(leftIconName)}
        <Text
          type="callout"
          size={buttonSmall ? 'small' : 'standard'}
          className={buttonStyleType === 'text' ? 'text-label' : ''}
        >
          {label}
        </Text>
        {rightIconName && renderSvgIcon(rightIconName)}
      </button>
      {isDropdownOpen && (
        <OptionsList
          inputId={label}
          options={dropdownOptions}
          rightAligned={rightAligned}
          dropdownSave={dropdownSave}
          isDropdownOpen={isDropdownOpen}
          toggleDropdown={toggleDropdown}
          onChange={handleOptionClick}
          dropdownTabIndex={0}
          ref={dropdownOptionsListRef}
        />
      )}
    </>
  );
};

export default Button;
